/**
 * Machine-readable deep link registry for tier-1 aniOS entities.
 * Human-readable contract: docs/navigation/deep-links.md
 *
 * `urlMapping` delegates parse/build for registered apps here so paths stay canonical.
 */

import type { WindowUrlConfig } from "@/components/ui/os-layout/urlMapping";
import { artifactCodec } from "@/navigation/apps/artifactLocation";
import { clientDetailCodec } from "@/navigation/apps/clientDetailLocation";
import { clientsListCodec } from "@/navigation/apps/clientsListLocation";
import { leadDetailCodec } from "@/navigation/apps/leadDetailLocation";
import { leadsListCodec } from "@/navigation/apps/leadsListLocation";
import { caseCheckingCodec } from "@/navigation/apps/caseCheckingLocation";
import { disclosureCodec } from "@/navigation/apps/disclosureLocation";
import {
  MEETING_APPROVAL_PATH_SUFFIXES,
  meetingCodec,
  meetingsCodec,
} from "@/navigation/apps/meetingsLocation";
import {
  missionControlCodec,
  missionControlTaskDetailPath,
  parseMissionControlTaskIdFromPath,
} from "@/navigation/apps/missionControlLocation";
import { missionDetailCodec } from "@/navigation/apps/missionDetailLocation";
import { guardiansCodec } from "@/navigation/apps/guardiansLocation";
import { playgroundCodec } from "@/navigation/apps/playgroundLocation";
import { screenerCodec } from "@/navigation/apps/screenerLocation";
import { triggersCodec } from "@/navigation/apps/triggersLocation";

export { missionControlTaskDetailPath, parseMissionControlTaskIdFromPath };

const REGISTRY_BUILD_APP_IDS = new Set([
  "mission-control",
  "mission",
  "clients",
  "client",
  "leads",
  "lead",
  "artifact",
  "case-checking",
  "disclosures",
  "meetings",
  "meeting",
  "triggers",
  "ops-guardians",
  "screener",
  "playground",
]);

/** Build a canonical relative URL for tier-1 registry apps, or null if not handled here. */
export function buildDeepLinkUrl(appId: string, params: Record<string, string>): string | null {
  if (!REGISTRY_BUILD_APP_IDS.has(appId)) {
    return null;
  }

  switch (appId) {
    case "mission-control":
      return missionControlCodec.toUrl(missionControlCodec.fromParams(params));
    case "mission":
      if (params.missionId) {
        return missionDetailCodec.toUrl(missionDetailCodec.fromParams(params));
      }
      return "/missions";
    case "clients":
      return clientsListCodec.toUrl(clientsListCodec.fromParams(params));
    case "lead":
      if (params.faClientId) {
        return leadDetailCodec.toUrl(leadDetailCodec.fromParams(params));
      }
      return "/leads";
    case "leads":
      return leadsListCodec.toUrl(leadsListCodec.fromParams(params));
    case "client":
      if (params.faClientId) {
        return clientDetailCodec.toUrl(clientDetailCodec.fromParams(params));
      }
      return "/clients";
    case "artifact":
      if (params.id) {
        return artifactCodec.toUrl(artifactCodec.fromParams(params));
      }
      return "/artifacts";
    case "case-checking":
      return caseCheckingCodec.toUrl(caseCheckingCodec.fromParams(params));
    case "disclosures":
      return disclosureCodec.toUrl(disclosureCodec.fromParams(params));
    case "meetings":
      return meetingsCodec.toUrl(meetingsCodec.fromParams(params));
    case "meeting":
      return meetingCodec.toUrl(meetingCodec.fromParams(params));
    case "triggers":
      return triggersCodec.toUrl(triggersCodec.fromParams(params));
    case "ops-guardians": {
      if (params.subRoute === "tickets") {
        const tParams = new URLSearchParams();
        if (params.ticket) tParams.set("ticket", params.ticket);
        const tSearch = tParams.toString();
        return tSearch ? `/ops/guardians/tickets?${tSearch}` : `/ops/guardians/tickets`;
      }
      return guardiansCodec.toUrl(guardiansCodec.fromParams(params));
    }
    case "screener":
      return screenerCodec.toUrl(screenerCodec.fromParams(params));
    case "playground":
      return playgroundCodec.toUrl(playgroundCodec.fromParams(params));
    default:
      return null;
  }
}

function mergePathAndSearch(pathname: string, search?: string): { pathOnly: string; searchParams: URLSearchParams } {
  const pathHasQuery = pathname.includes("?");
  const pathOnly = pathHasQuery ? pathname.slice(0, pathname.indexOf("?")) : pathname;
  const queryFromPath = pathHasQuery ? pathname.slice(pathname.indexOf("?") + 1) : "";
  const searchMerged = [queryFromPath, (search || "").replace(/^\?/, "")].filter(Boolean).join("&");
  return { pathOnly, searchParams: new URLSearchParams(searchMerged) };
}

/** Parse tier-1 deep link paths into window config, or null to fall through to legacy urlMapping. */
export function parseDeepLinkUrl(pathname: string, search?: string): WindowUrlConfig | null {
  const { pathOnly, searchParams } = mergePathAndSearch(pathname, search);
  const pathSegments = pathOnly.split("/").filter(Boolean);
  if (pathSegments.length === 0) {
    return null;
  }

  const basePath = pathSegments[0];
  const resourceId = pathSegments[1];

  switch (basePath) {
    case "mission-control":
      return {
        appId: "mission-control",
        title: "Mission Control",
        params: missionControlCodec.toParams(
          missionControlCodec.fromUrl(pathOnly, searchParams),
        ),
      };
    case "tasks": {
      const legacyTaskId = parseMissionControlTaskIdFromPath(pathOnly);
      if (legacyTaskId) {
        return {
          appId: "mission-control",
          title: "Mission Control",
          params: missionControlCodec.toParams(
            missionControlCodec.fromUrl(pathOnly, searchParams),
          ),
        };
      }
      return null;
    }
    case "missions": {
      if (resourceId) {
        return {
          appId: "mission",
          title: "Mission",
          params: missionDetailCodec.toParams(
            missionDetailCodec.fromUrl(pathOnly, searchParams),
          ),
        };
      }
      const missionsListFocus = searchParams.get("focus");
      if (missionsListFocus?.startsWith("mission:")) {
        return {
          appId: "mission-control",
          title: "Mission Control",
          params: missionControlCodec.toParams(
            missionControlCodec.fromUrl(pathOnly, searchParams),
          ),
        };
      }
      return {
        appId: "missions",
        title: "Missions",
        params: missionsListFocus ? { focus: missionsListFocus } : {},
      };
    }
    case "clients": {
      if (resourceId) {
        return {
          appId: "client",
          title: "Client",
          params: clientDetailCodec.toParams(
            clientDetailCodec.fromUrl(pathOnly, searchParams),
          ),
        };
      }
      return {
        appId: "clients",
        title: "Clients",
        params: clientsListCodec.toParams(
          clientsListCodec.fromUrl(pathOnly, searchParams),
        ),
      };
    }
    case "leads": {
      if (resourceId) {
        return {
          appId: "lead",
          title: "Lead",
          params: leadDetailCodec.toParams(
            leadDetailCodec.fromUrl(pathOnly, searchParams),
          ),
        };
      }
      return {
        appId: "leads",
        title: "Leads",
        params: leadsListCodec.toParams(
          leadsListCodec.fromUrl(pathOnly, searchParams),
        ),
      };
    }
    case "artifacts": {
      if (resourceId) {
        const artifactLoc = artifactCodec.fromUrl(pathOnly, searchParams);
        const title =
          artifactLoc.view === "handover-approval" ? "Handover approval" : "Artifact";
        return {
          appId: "artifact",
          title,
          params: artifactCodec.toParams(artifactLoc),
        };
      }
      return { appId: "artifacts", title: "Artifacts", params: {} };
    }
    case "case-checking": {
      const legacyOpsView =
        resourceId === "costs" || resourceId === "comparison" ? resourceId : searchParams.get("view");
      if (legacyOpsView === "costs" || legacyOpsView === "comparison") {
        return { appId: "ops-case-checking", title: "Case Checking Ops", params: { view: legacyOpsView } };
      }
      const loc = caseCheckingCodec.fromUrl(pathOnly, searchParams);
      return {
        appId: "case-checking",
        title: loc.view === "rules" ? "Case Checking Rules" : "Case Checking",
        params: caseCheckingCodec.toParams(loc),
      };
    }
    case "disclosures": {
      const loc = disclosureCodec.fromUrl(pathOnly, searchParams);
      return {
        appId: "disclosures",
        title: loc.caseId ? "Disclosure" : "Disclosures",
        params: disclosureCodec.toParams(loc),
      };
    }
    case "meetings": {
      const thirdSegment = pathSegments[2];
      const viewParam = searchParams.get("view");
      const pathApproval =
        thirdSegment != null && MEETING_APPROVAL_PATH_SUFFIXES.has(thirdSegment);
      const queryApproval =
        viewParam != null && MEETING_APPROVAL_PATH_SUFFIXES.has(viewParam);
      const opensMeetingWindow =
        resourceId != null &&
        resourceId !== "calendar" &&
        (viewParam === "full" || pathApproval || queryApproval);
      if (opensMeetingWindow) {
        return {
          appId: "meeting",
          title: "Meeting",
          params: meetingCodec.toParams(meetingCodec.fromUrl(pathOnly, searchParams)),
        };
      }
      const loc = meetingsCodec.fromUrl(pathOnly, searchParams);
      return {
        appId: "meetings",
        title: "Meetings",
        params: meetingsCodec.toParams(loc),
      };
    }
    case "screener":
      return {
        appId: "screener",
        title: "Screener",
        params: screenerCodec.toParams(screenerCodec.fromUrl(pathOnly, searchParams)),
      };
    case "playground":
      return {
        appId: "playground",
        title: "Playground",
        params: playgroundCodec.toParams(playgroundCodec.fromUrl(pathOnly, searchParams)),
      };
    case "ops": {
      if (resourceId === "triggers") {
        return {
          appId: "triggers",
          title: "Triggers",
          params: triggersCodec.toParams(triggersCodec.fromUrl(pathOnly, searchParams)),
        };
      }
      if (resourceId === "guardians") {
        const thirdSegment = pathSegments[2];
        if (thirdSegment === "screenings") {
          return {
            appId: "ops-guardians",
            title: "The Guardians",
            params: guardiansCodec.toParams(guardiansCodec.fromUrl(pathOnly, searchParams)),
          };
        }
        if (thirdSegment === "v2" && pathSegments[3] === "tickets") {
          const ticket = searchParams.get("ticket");
          return {
            appId: "ops-guardians",
            title: "Guardian Tickets",
            params: {
              subRoute: "tickets",
              ...(ticket ? { ticket } : {}),
            },
          };
        }
        if (thirdSegment === "tickets") {
          const ticket = searchParams.get("ticket");
          return {
            appId: "ops-guardians",
            title: "Guardian Tickets",
            params: {
              subRoute: "tickets",
              ...(ticket ? { ticket } : {}),
            },
          };
        }
        if (thirdSegment === "conversation") {
          const mission = searchParams.get("mission");
          const task = searchParams.get("task");
          const focus = searchParams.get("focus");
          return {
            appId: "ops-guardians-conversation",
            title: "Conversation",
            params: {
              ...(mission ? { mission } : {}),
              ...(task ? { task } : {}),
              ...(focus ? { focus } : {}),
            },
          };
        }
        if (!thirdSegment) {
          const view = searchParams.get("view");
          const mission = searchParams.get("mission");
          const task = searchParams.get("task");
          if (view === "conversation") {
            return {
              appId: "ops-guardians-conversation",
              title: "Conversation",
              params: {
                ...(mission ? { mission } : {}),
                ...(task ? { task } : {}),
              },
            };
          }
          return {
            appId: "ops-guardians",
            title: "The Guardians",
            params: guardiansCodec.toParams(guardiansCodec.fromUrl(pathOnly, searchParams)),
          };
        }
      }
      return null;
    }
    default:
      return null;
  }
}
